<script setup lang="ts">
import { ref } from 'vue'
import { MarButton, MarDialog, MarField, MarForm, MarIconBtn, MarIconPicker, MarListGroup, MarTextInput, MarSidebarEdit, type IconType } from '@nimling/components-nimtech'
import PageTreeItem from '@/components/PageTreeItem.vue'
import type { SidebarGroup } from '@/types'
import { useUiStore } from '@/stores/ui'

const emit = defineEmits<{ close: [result?: unknown] }>()
const uiStore = useUiStore()

const editingId = ref<string | null>(null)
const groupName = ref('')
const groupIcon = ref('folder')

const editGroup = (group: SidebarGroup) => {
  editingId.value = group.id
  groupName.value = group.label
  groupIcon.value = group.icon
}

const resetGroup = () => {
  editingId.value = null
  groupName.value = ''
  groupIcon.value = 'folder'
}

const putGroup = () => {
  const label = groupName.value.trim()
  if (!label) return
  if (editingId.value) uiStore.putSidebarGroup(editingId.value, label, groupIcon.value)
  else uiStore.postSidebarGroup(label, groupIcon.value)
  resetGroup()
}

const deleteGroup = (id: string) => {
  if (editingId.value === id) resetGroup()
  uiStore.deleteSidebarGroup(id)
}
</script>

<template>
  <MarDialog title="Edit sidebar" @close="emit('close')">
    <div class="sidebar-dialog">
      <MarForm @keydown.enter="putGroup">
        <MarField label="Section">
          <div class="sidebar-dialog-section">
            <MarIconPicker v-model="groupIcon"><MarTextInput v-model="groupName" placeholder="Section name" autofocus /></MarIconPicker>
            <MarIconBtn icon="check" :tooltip="editingId ? 'Save section' : 'Create section'" :disabled="!groupName.trim()" @click="putGroup" />
            <MarIconBtn v-if="editingId" icon="cross" tooltip="Cancel" @click="resetGroup" />
          </div>
        </MarField>
      </MarForm>

      <div class="sidebar-dialog-list">
        <MarSidebarEdit nest @reorder="uiStore.putSidebarReorder">
          <MarListGroup
            v-for="group in uiStore.sidebarSections"
            :id="group.id"
            :key="group.id"
            :label="group.label"
            :icon="group.icon as IconType"
          >
            <template #actions>
              <MarIconBtn icon="pencil" tooltip="Rename section" :active="editingId === group.id" @click.stop="editGroup(group)" />
              <MarIconBtn icon="trash" severity="danger" tooltip="Delete section" @click.stop="deleteGroup(group.id)" />
            </template>
            <PageTreeItem v-for="page in group.pages" :key="page.id" :page="page" :tree="uiStore.sidebarPageTree" edit />
          </MarListGroup>
          <PageTreeItem v-for="page in uiStore.sidebarPages" :key="page.id" :page="page" :tree="uiStore.sidebarPageTree" edit />
        </MarSidebarEdit>
      </div>
    </div>

    <template #footer>
      <MarButton label="Done" @click="emit('close')" />
    </template>
  </MarDialog>
</template>

<style scoped>
.sidebar-dialog {
  width: min(28em, 85vw);
  display: flex;
  flex-direction: column;
  gap: 1em;
}

.sidebar-dialog-section {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;
  gap: 0.25em;
}

.sidebar-dialog-list {
  max-height: 50vh;
  overflow-y: auto;
}
</style>
